import React from 'react';
import { Button } from './ui/button';
import { WarningCircle, ArrowClockwise, House } from '@phosphor-icons/react';

const MAX_TRANSLATE_RETRIES = 10;

/**
 * Detecta si el error viene del clásico conflicto React vs Google Translate
 * (Chrome mobile). Ante ese caso NO rompemos la pantalla: intentamos remontar
 * el subtree silenciosamente.
 */
const isTranslateDomError = (err) => {
  if (!err) return false;
  const msg = (err.message || err.toString() || '').toLowerCase();
  return (
    msg.includes('removechild') ||
    msg.includes('insertbefore') ||
    msg.includes('the node to be removed is not a child') ||
    msg.includes('nodo que se va a eliminar') ||
    msg.includes('node to insert before is not a child')
  );
};

/**
 * Top-level error boundary. Catches any unhandled JS exception from a React
 * subtree so the user never sees a totally blank screen.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, remountKey: 0 };
    this._retries = 0;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidUpdate(_prevProps, prevState) {
    // 🔄 Cuando el boundary consigue recuperarse (hasError: true → false),
    // reseteamos el contador de reintentos. Así, futuros errores nuevos
    // pueden volver a intentar hasta MAX_TRANSLATE_RETRIES.
    if (prevState.hasError && !this.state.hasError) {
      this._retries = 0;
    }
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);

    // 🛡 Auto-recover: si es un DOM error causado por el traductor del navegador,
    // remontamos el subtree hasta MAX_TRANSLATE_RETRIES veces antes de mostrar el fallback.
    if (isTranslateDomError(error) && this._retries < MAX_TRANSLATE_RETRIES) {
      this._retries += 1;
      // Limpiamos el hasError en el próximo tick para forzar remount.
      setTimeout(() => {
        this.setState((s) => ({
          hasError: false,
          error: null,
          remountKey: s.remountKey + 1,
        }));
      }, 50);
    }
  }

  handleReset = () => {
    this.setState((s) => ({ hasError: false, error: null, remountKey: s.remountKey + 1 }));
  };

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      // key + fragmento para permitir el remount forzado sin quebrar el árbol
      return <React.Fragment key={this.state.remountKey}>{this.props.children}</React.Fragment>;
    }

    const message =
      this.state.error?.message ||
      this.state.error?.toString?.() ||
      'Ocurrió un error inesperado';

    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-12 bg-background notranslate"
        translate="no"
        data-testid="error-boundary-fallback"
      >
        <div className="w-full max-w-lg text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-destructive/10 border border-destructive/30 flex items-center justify-center">
              <WarningCircle size={48} weight="fill" className="text-destructive" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-3">Algo salió mal</h1>
          <p className="text-muted-foreground mb-2">
            La página tuvo un problema cargando. No te preocupes, tu pedido (si lo hiciste) está
            guardado.
          </p>
          <p className="text-xs text-muted-foreground font-mono mb-6 p-3 bg-muted/50 rounded-md break-words">
            {message}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={this.handleReset} data-testid="error-retry-button">
              <ArrowClockwise size={16} className="mr-2" />
              Reintentar
            </Button>
            <Button variant="outline" onClick={this.handleReload} data-testid="error-reload-button">
              <ArrowClockwise size={16} className="mr-2" />
              Recargar la página
            </Button>
            <Button variant="outline" onClick={this.handleHome} data-testid="error-home-button">
              <House size={16} className="mr-2" />
              Volver al inicio
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-6">
            Si el problema persiste, escribinos por WhatsApp y te ayudamos.
          </p>
        </div>
      </div>
    );
  }
}
